/**
 * Reusable JSON-LD structured data schemas for Sullivan Care.
 * Import individual schemas in page components and pass to <JsonLd />.
 */

const SITE_URL = "https://sullivancare.com";
const LOGO_URL = `${SITE_URL}/og-logo.png`;
const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/p8gjgWQbB1SHxjogTIPYrUbABN83/social-images/social-1772903241042-615e9512-c626-46da-b14e-c0120c5d08f6.webp";

/* ── Site-wide Organization ── */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Sullivan Care",
  url: SITE_URL,
  logo: LOGO_URL,
  image: OG_IMAGE,
  description:
    "Sullivan Care builds and manages growth-driven software systems for independent pharmacies and specialty clinics.",
  sameAs: [
    "https://www.facebook.com/sullivancare",
    "https://www.linkedin.com/company/sullivancare",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-800-000-0000",
    contactType: "sales",
    areaServed: "US",
    availableLanguage: "English",
  },
};

/* ── WebSite (homepage) ── */
export const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Sullivan Care",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

/* ── LocalBusiness (Contact page) ── */
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Sullivan Care",
  url: SITE_URL,
  logo: LOGO_URL,
  image: OG_IMAGE,
  description:
    "Managed healthcare software infrastructure for pharmacies and clinics.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "US",
  },
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  priceRange: "$$",
};

/* ── BreadcrumbList helper ── */
export function breadcrumbSchema(
  items: { name: string; path: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

/* ── Service schema helper ── */
export function serviceSchema(
  name: string,
  description: string,
  url: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${url}`,
    provider: {
      "@type": "Organization",
      name: "Sullivan Care",
      url: SITE_URL,
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
  };
}

/* ── FAQPage schema ── */
export function faqPageSchema(
  faqs: { q: string; a: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };
}
